import React, { FC, useState, useEffect } from 'react'
import Box from 'transfix-ui/core/Box'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import {
  SpotBiddingRule,
  SpotBiddingConfig,
  useGetSpotBiddingConfig,
} from 'apps/marketplace/apiTypes/spotBiddingConfigs'
import { IShipper } from 'apps/spot-quote-control-panel/types/shipperStrikePrices'
import { getDnbRegions } from '../utils/shipperStrikePrice'
import { StrikePriceRulesSidebar } from './StrikePriceRulesSidebar'
import { StrikePriceShipperResults } from './StrikePriceShipperResults'

const useStyles = makeStyles({
  container: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${brandColors.coolGray4}`,
  },
})

const useGlobalSpotBiddingConfigs = () => {
  const {
    data: globalConfigs,
    error: globalConfigsError,
    loading: globalConfigsLoading,
    refetch: fetchGlobalConfigs,
  } = useGetSpotBiddingConfig({
    queryParams: {
      filters: {
        namespace: 'strike_price_panel',
        configType: 'GLOBAL',
      },
    },
  })

  return {
    globalConfigs,
    globalConfigsLoading,
    globalConfigsError,
    fetchGlobalConfigs,
  }
}

const useShipperSpotBiddingConfigs = (currentShipperId: number) => {
  const {
    data: shipperConfigs,
    error: shipperConfigsError,
    loading: shipperConfigsLoading,
    refetch: fetchShipperConfigs,
  } = useGetSpotBiddingConfig({
    queryParams: {
      filters: {
        namespace: 'strike_price_panel',
        configType: 'SHIPPER',
        shipperApplicationId: currentShipperId,
      },
    },
    lazy: true,
  })

  return {
    shipperConfigs,
    shipperConfigsLoading,
    shipperConfigsError,
    fetchShipperConfigs,
  }
}

const getSpotBiddingConfigRules = (configs: SpotBiddingConfig[]): SpotBiddingRule[] => {
  return configs.reduce<SpotBiddingRule[]>((all, current) => {
    return [...all, ...(current.rules || [])]
  }, [])
}

export const StrikePriceControlPanel: FC = () => {
  const classes = useStyles()
  const [currentShipper, setCurrentShipper] = useState<IShipper | undefined>(undefined)
  const {
    fetchGlobalConfigs,
    globalConfigs,
    globalConfigsError,
    globalConfigsLoading,
  } = useGlobalSpotBiddingConfigs()
  const {
    fetchShipperConfigs,
    shipperConfigs,
    shipperConfigsError,
    shipperConfigsLoading,
  } = useShipperSpotBiddingConfigs(currentShipper?.id || 0)

  const globalRules = getSpotBiddingConfigRules(globalConfigs?.spotBiddingConfigs || [])
  const shipperRules = getSpotBiddingConfigRules(shipperConfigs?.spotBiddingConfigs || [])

  // Find all regions with an associated DNB rule; the result
  // tables will use this to indicate if we are not bidding on
  // a region:
  const allRules = [...globalRules, ...shipperRules]
  const dnbRegions = getDnbRegions(allRules)

  const reloadRules = () => {
    fetchGlobalConfigs()
    if (currentShipper?.id) {
      fetchShipperConfigs()
    }
  }

  // Wait to fetch shipper configs until a shipper
  // has actually been specified from the UI; otherwise
  // we would be returning every strike price for
  // every shipper:
  useEffect(() => {
    if (currentShipper?.id) {
      fetchShipperConfigs()
    }
  }, [currentShipper])

  return (
    <Box className={classes.container} data-test='strike-price-control-panel'>
      <StrikePriceRulesSidebar
        currentShipper={currentShipper}
        globalRules={globalRules}
        globalRulesLoading={globalConfigsLoading}
        globalRulesError={globalConfigsError?.message}
        shipperRules={shipperRules}
        shipperRulesLoading={shipperConfigsLoading}
        shipperRulesError={shipperConfigsError?.message}
        reloadRules={reloadRules}
      />
      <Box p={4} flexGrow={1}>
        <StrikePriceShipperResults
          currentShipper={currentShipper}
          onSetCurrentShipper={setCurrentShipper}
          doNotBidRegions={dnbRegions}
        />
      </Box>
    </Box>
  )
}
